// Aufgeteilt aus dem früheren 2600-Zeilen-Monolithen `musicXmlDom.ts` (Import-Pfad unverändert:
// `@/composables/musicXmlDom` zeigt auf `index.ts`). Abhängigkeitsregel im Ordner: geteilte
// Basis-Helfer liegen in `core.ts`; gerichtete Geschwister-Imports sind erlaubt, ZYKLEN nicht
// (aktuell: bass→chordInference, notes→core, diatonic→notes, structure→notes,
// lyrics→bass, clipboard→structure, fragment→structure).
import {BASS_LYRIC_NAME} from './bass';
import {type NoteInfo, chordGroupOf, findPartAndMeasureNote, parseMusicXml, serializeMusicXml, voiceOf} from './core';

// ============ Gesangstext (Strophen-Lyrics) ============

/** Lyric-Slots, die die **Engine** belegt – niemals als Strophe beschreiben. Spiegel von
 *  `griffschrift/mapping/bass.py` (3 = Bass-Zeile) und `mapping/button_model.py`
 *  (10 = Druck-Unterstrich, 11 = Notennamen, 12 = DebugScore, 13 = NotFound-Notenname). */
export const RESERVED_LYRIC_NUMBERS: readonly string[] = ['3', '10', '11', '12', '13'];

/** Strophen, die der Editor anbietet. **Die Zählung beginnt bei 4**, nicht bei 1:
 *  - **1-2** gehören dem **Original-Gesangstext der Quelldatei** (`Lyrics.Preserve` kopiert die
 *    Nummern des Quelldokuments unverändert in die Ausgabe) – der Editor darf sie nie überschreiben.
 *  - **3** ist die Bass-Zeile der Griffschrift; `remove_existing_bass_annotations` löscht jede
 *    Lyric mit `number == 3`.
 *  Die angezeigte Beschriftung ist deshalb `number - BASS_LYRIC_NUMBER` (4 → „Strophe 1"), siehe
 *  `ScoreEditToolbar.vue`. Die literalen Werte pinnt `musicXmlDom.spec.ts` an genau einer Stelle;
 *  alles andere leitet aus dieser Konstante ab. */
export const EDITABLE_VERSE_NUMBERS = ['4', '5', '6'] as const;

export type Syllabic = 'single' | 'begin' | 'middle' | 'end';

/** Gelesener Zustand einer Strophen-Lyric. */
export interface VerseLyric {
    text: string;
    syllabic: Syllabic;
    extend: boolean;
}

/** Was der Editor pro Note liefert. `continuesWord` = die Silbe wurde mit `-` abgeschlossen,
 *  das Wort geht auf der nächsten Note weiter; der Silbentyp folgt daraus + dem Vorgänger. */
export interface VerseLyricInput {
    text: string;
    continuesWord: boolean;
    extend: boolean;
}

export function isReservedLyricNumber(verse: string): boolean {
    return RESERVED_LYRIC_NUMBERS.includes(verse.trim());
}

/** Strophennummer einer `<lyric>`; fehlt sie, gilt der MusicXML-Default `1` (wie OSMDs LyricsReader). */
function verseNumberOf(lyric: Element): string {
    return lyric.getAttribute('number')?.trim() || '1';
}

function syllabicOf(lyric: Element): Syllabic {
    const raw = lyric.querySelector('syllabic')?.textContent?.trim();
    return raw === 'begin' || raw === 'middle' || raw === 'end' ? raw : 'single';
}

/** Die `<lyric>` der Strophe `verse` einer `<note>`, sonst `null`. Die Bass-Zeile (`name="Bass"`)
 *  wird **immer** ausgeschlossen – eine Alt-Datei mit `name="Bass"` ohne `number` würde sonst als
 *  Strophe 1 gelten. `querySelectorAll` statt tagName-Vergleich (happy-dom schreibt erzeugte Tags groß). */
export function verseLyricOf(noteEl: Element, verse: string): Element | null {
    const wanted = verse.trim();
    return [...noteEl.querySelectorAll('lyric')].find(
        (l) => l.getAttribute('name') !== BASS_LYRIC_NAME && verseNumberOf(l) === wanted,
    ) ?? null;
}

/**
 * Silbentyp aus dem Silbentyp der **Vorgängernote** und „das Wort geht weiter" (Eingabe mit `-`).
 * `previous === null` = der Vorgänger trägt in dieser Strophe keine Silbe (oder es gibt keinen)
 * → das Wort beginnt hier. Die Invariante ist **lokal**, deshalb genügt beim Editieren die
 * Reparatur der direkten Nachbarn (siehe `setVerseLyricInMusicXml`).
 */
export function syllabicFor(previous: Syllabic | null, continuesWord: boolean): Syllabic {
    const inWord = previous === 'begin' || previous === 'middle';
    if (inWord) return continuesWord ? 'middle' : 'end';
    return continuesWord ? 'begin' : 'single';
}

/** Vorige (`-1`) bzw. nächste (`1`) Note derselben Stimme im Part – über Taktgrenzen hinweg,
 *  `<chord/>`-Begleiter übersprungen. **Pausen werden NICHT übersprungen**: eine Pause trägt
 *  normalerweise keine Strophen-Lyric und beendet damit das Wort (gewolltes Verhalten). */
function siblingVerseNote(part: Element, noteEl: Element, voice: number, dir: -1 | 1): Element | null {
    const all = [...part.querySelectorAll('note')];
    const idx = all.indexOf(noteEl);
    if (idx < 0) return null;
    const range = dir < 0 ? all.slice(0, idx).reverse() : all.slice(idx + 1);
    return range.find((n) => voiceOf(n) === voice && !n.querySelector('chord')) ?? null;
}

function syllabicAt(noteEl: Element | null, verse: string): Syllabic | null {
    if (!noteEl) return null;
    const lyric = verseLyricOf(noteEl, verse);
    return lyric ? syllabicOf(lyric) : null;
}

function setSyllabicElement(doc: Document, lyric: Element, syllabic: Syllabic): void {
    let el: Element | null = lyric.querySelector('syllabic');
    if (!el) {
        el = doc.createElement('syllabic');
        lyric.insertBefore(el, lyric.firstChild);
    }
    el.textContent = syllabic;
}

/** Hängt eine neue `<lyric>` schema-korrekt ein: nach den Geschwistern mit kleinerer Nummer, vor
 *  denen mit größerer, in jedem Fall vor `<play>`/`<listen>` (MusicXML-Reihenfolge im `<note>`). */
function insertLyricInVerseOrder(noteEl: Element, lyric: Element, verse: string): void {
    const num = parseInt(verse, 10);
    const later = [...noteEl.querySelectorAll('lyric')].find(
        (l) => (parseInt(verseNumberOf(l), 10) || 1) > num,
    );
    if (later) {
        noteEl.insertBefore(lyric, later);
        return;
    }
    const tail = [...noteEl.children].find((c) => ['play', 'listen'].includes(c.tagName.toLowerCase()));
    if (tail) noteEl.insertBefore(lyric, tail);
    else noteEl.appendChild(lyric);
}

/** Schreibt (`value`) bzw. entfernt (`null`) die Strophen-Lyric EINER `<note>`. Der Inhalt wird in
 *  Schema-Reihenfolge `<syllabic>`, `<text>`, `<extend/>` neu aufgebaut – ein zusammengesetztes
 *  Lyric (`<elision>`) wird dabei bewusst zu einer einfachen Silbe vereinfacht. */
function writeVerseLyric(doc: Document, noteEl: Element, verse: string, value: VerseLyric | null): void {
    const existing = verseLyricOf(noteEl, verse);
    if (!value) {
        existing?.remove();
        return;
    }
    let lyric = existing;
    if (!lyric) {
        lyric = doc.createElement('lyric');
        lyric.setAttribute('number', verse);
        insertLyricInVerseOrder(noteEl, lyric, verse);
    }
    while (lyric.firstChild) lyric.removeChild(lyric.firstChild);
    const syllabic = doc.createElement('syllabic');
    syllabic.textContent = value.syllabic;
    const text = doc.createElement('text');
    text.textContent = value.text;
    lyric.appendChild(syllabic);
    lyric.appendChild(text);
    // `<extend/>` bewusst BARE (ohne `type`) am Ende der eigenen Silbe – OSMD wertet nur die
    // Anwesenheit aus und ignoriert eine textlose `type="stop"`-Fortsetzung komplett.
    if (value.extend) lyric.appendChild(doc.createElement('extend'));
}

/** Nachbar-Reparatur nach vorn: der Silbentyp der Folgenote hängt am neuen Typ der bearbeiteten. */
function repairFollowingSyllabic(
    doc: Document, part: Element, noteEl: Element, voice: number, verse: string, previous: Syllabic | null,
): void {
    const next = siblingVerseNote(part, noteEl, voice, 1);
    const lyric = next ? verseLyricOf(next, verse) : null;
    if (!lyric) return;
    const current = syllabicOf(lyric);
    setSyllabicElement(doc, lyric, syllabicFor(previous, current === 'begin' || current === 'middle'));
}

/**
 * Setzt (`value`) oder entfernt (`null`/leerer Text) die Strophen-Lyric **einer** Note der Strophe
 * `verse`. Bewusst eine einzelne `NoteInfo` (anders als `setBassLyricInMusicXml`): eine Silbe über
 * eine Mehrfachauswahl zu verteilen ist musikalisch sinnlos, und die Nachbar-Reparatur ist für
 * genau eine Note definiert. Akkordtöne werden auf die Primärnote der Gruppe umgeleitet.
 *
 * Der **Silbentyp wird nie übergeben, sondern abgeleitet**: aus dem Vorgänger + `continuesWord`
 * (`syllabicFor`), danach wird der direkte Nachfolger nachgezogen (die Invariante ist lokal). Beim
 * Löschen wird zusätzlich der Vorgänger geschlossen (`begin→single`, `middle→end`).
 *
 * **Melisma erzwingt `single`/`end`** (`continuesWord` wird auf `false` gezwungen): OSMD wertet
 * `<extend/>` nur bei diesen Silbentypen aus, mitten im Wort bliebe es unsichtbar.
 *
 * Nur `<lyric>` ändert sich – **nie `<duration>`** –, daher bleibt die Taktlänge gültig. `null` bei
 * reserviertem Slot (3, 10–13), unauflösbarer Adresse oder Parse-Fehler.
 */
export function setVerseLyricInMusicXml(
    musicXml: string,
    noteInfo: NoteInfo,
    value: VerseLyricInput | null,
    verse: string,
): string | null {
    if (isReservedLyricNumber(verse)) return null;
    try {
        const doc = parseMusicXml(musicXml);
        const found = findPartAndMeasureNote(doc, noteInfo);
        if (!found) return null;
        const target = chordGroupOf(found.note)[0] ?? found.note;
        const voice = voiceOf(target);
        const previous = siblingVerseNote(found.part, target, voice, -1);
        const previousSyllabic = syllabicAt(previous, verse);
        const text = (value?.text ?? '').trim();

        if (!text) {
            writeVerseLyric(doc, target, verse, null);
            const previousLyric = previous ? verseLyricOf(previous, verse) : null;
            if (previousLyric) {
                const current = syllabicOf(previousLyric);
                if (current === 'begin') setSyllabicElement(doc, previousLyric, 'single');
                else if (current === 'middle') setSyllabicElement(doc, previousLyric, 'end');
            }
            repairFollowingSyllabic(doc, found.part, target, voice, verse, null);
        } else {
            const extend = !!value?.extend;
            const continuesWord = extend ? false : !!value?.continuesWord;
            const syllabic = syllabicFor(previousSyllabic, continuesWord);
            writeVerseLyric(doc, target, verse, {text, syllabic, extend});
            repairFollowingSyllabic(doc, found.part, target, voice, verse, syllabic);
        }
        return serializeMusicXml(doc);
    } catch (e) {
        console.error('Error setting verse lyric in MusicXML:', e);
        return null;
    }
}

/** Strophen-Lyric der adressierten Note (Akkordton → Primärnote); `null` ohne Lyric, bei
 *  reserviertem Slot oder unauflösbarer Adresse. */
export function readVerseLyric(musicXml: string, noteInfo: NoteInfo, verse: string): VerseLyric | null {
    if (isReservedLyricNumber(verse)) return null;
    try {
        const doc = parseMusicXml(musicXml);
        const found = findPartAndMeasureNote(doc, noteInfo);
        if (!found) return null;
        const primary = chordGroupOf(found.note)[0] ?? found.note;
        const lyric = verseLyricOf(primary, verse);
        if (!lyric) return null;
        return {
            text: lyric.querySelector('text')?.textContent ?? '',
            syllabic: syllabicOf(lyric),
            extend: !!lyric.querySelector('extend'),
        };
    } catch (e) {
        console.error('Error reading verse lyric from MusicXML:', e);
        return null;
    }
}
